import { Mail, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuthContext } from '../../contexts/AuthContext';

export function AuthModal() {
  const {
    showAuthModal,
    closeAuthModal,
    authEmail,
    setAuthEmail,
    authError,
    setAuthError,
    authLoading,
    authEmailSent,
    setAuthEmailSent,
    sendMagicLink,
  } = useAuthContext();

  if (!showAuthModal) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-slate-900 to-black rounded-3xl max-w-md w-full border border-white/10 p-8 relative">
        <button
          onClick={closeAuthModal}
          className="absolute top-3 right-3 text-slate-400 hover:text-white p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-lg hover:bg-white/5"
          aria-label="Close"
        >
          <span className="text-xl">✕</span>
        </button>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 mb-4">
            <Mail className="h-8 w-8 text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold mb-2">
            {authEmailSent ? 'Check Your Email' : 'Sign In'}
          </h2>
          <p className="text-slate-400">
            {authEmailSent
              ? `We sent a magic link to ${authEmail}`
              : 'Sign in to sync your credits across devices'}
          </p>
        </div>

        {authEmailSent ? (
          <div className="space-y-4">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4 text-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-sm text-slate-300">
                Click the link in your email to sign in. The link expires in 15 minutes.
              </p>
            </div>

            <button
              onClick={() => setAuthEmailSent(false)}
              className="w-full py-3 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-all text-sm"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                value={authEmail}
                onChange={(e) => {
                  setAuthEmail(e.target.value);
                  setAuthError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && sendMagicLink()}
                placeholder="Enter your email"
                className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500 text-base"
                style={{ fontSize: '16px' }}
                disabled={authLoading}
                enterKeyHint="send"
              />
              {authError && (
                <p className="text-red-400 text-sm mt-2">{authError}</p>
              )}
            </div>

            <button
              onClick={sendMagicLink}
              disabled={authLoading}
              className="w-full py-3 rounded-lg bg-gradient-to-r from-emerald-500 to-green-500 hover:from-emerald-600 hover:to-green-600 font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {authLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Magic Link'
              )}
            </button>

            <div className="text-center text-xs text-slate-500 pt-4 border-t border-white/10">
              <p>No password needed! We'll email you a secure sign-in link.</p>
              <p className="mt-2">Your credits will sync across all your devices.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
